import { useEffect, useState } from 'react'
import { engineRulesAPI } from '../../services/api'
import type { EnginePause } from '../../types'
import TerminalPanel from '../dashboard/TerminalPanel'
import { formatDeciderTime } from './Phase2DeciderNote'

const WHO: Record<string, string> = {
  breaker: 'the automatic breaker',
  admin: 'an admin',
  other: 'the platform',
}

/** "Oct 25, 2026 14:00 UTC → Oct 27, 2026 09:00 UTC", or "… → still paused". */
export const pausePeriod = (pause: EnginePause): string => {
  const start = pause.started_at ? formatDeciderTime(pause.started_at) : null
  const end = pause.ended_at ? formatDeciderTime(pause.ended_at) : null
  return `${start ?? 'Unknown start'} → ${pause.ended_at ? end ?? 'unknown end' : 'still paused'}`
}

/**
 * Every period the engine published nothing (GET /api/public/engine-pauses),
 * so a gap in the record is never unexplained: when, who paused it and why.
 * What the paused engine would have published is deliberately not here:
 * it is hypothetical, and only real signals stand beside the record. Nothing
 * renders when the engine has never been paused, or if the history can't load.
 */
const EnginePausesPanel = () => {
  const [pauses, setPauses] = useState<EnginePause[]>([])

  useEffect(() => {
    let live = true
    Promise.resolve()
      .then(() => engineRulesAPI.pauses())
      .then((rows) => live && setPauses(rows ?? []))
      .catch(() => {})
    return () => {
      live = false
    }
  }, [])

  if (!pauses.length) return null
  return (
    <TerminalPanel
      title={`Engine pauses (${pauses.length})`}
      tone="amber"
      subtitle={
        <p className="text-xs text-gray-400">
          While paused the engine publishes nothing, so these periods have no signals. Each pause is listed with why it
          started, as recorded.
        </p>
      }
    >
      <ul className="space-y-2 text-sm" data-testid="engine-pauses">
        {pauses.map((pause, i) => (
          <li key={`${pause.started_at}-${i}`} className="text-gray-300">
            <span className="font-mono text-xs text-gray-200">{pausePeriod(pause)}</span>
            <span className="text-gray-400">
              {' '}
              · paused by {WHO[pause.paused_by] ?? WHO.other}
              {pause.resumed_by ? `, resumed by ${WHO[pause.resumed_by] ?? WHO.other}` : ''}
            </span>
            {pause.reason && <p className="mt-0.5 text-xs text-gray-400">Why: {pause.reason}</p>}
          </li>
        ))}
      </ul>
    </TerminalPanel>
  )
}

export default EnginePausesPanel
